import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { useBodyLock } from '../hooks/useBodyLock';
import { useMobileOptimizations } from '../hooks/useMobileOptimizations';

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  maxHeight?: string;
  fullHeight?: boolean;
  className?: string;
}

const BottomSheet: React.FC<BottomSheetProps> = ({
  isOpen,
  onClose,
  title,
  children,
  maxHeight = '90vh',
  fullHeight = false,
  className = ''
}) => {
  const { isMobile } = useMobileOptimizations();
  const [isRendered, setIsRendered] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const sheetRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<number>(0);
  const currentYRef = useRef<number>(0);
  const isDraggingRef = useRef<boolean>(false);

  useBodyLock(isOpen);

  // Animações de montar/desmontar
  useEffect(() => {
    if (isOpen) {
      setIsRendered(true);
      requestAnimationFrame(() => requestAnimationFrame(() => setIsVisible(true)));
    } else {
      setIsVisible(false);
      const timer = setTimeout(() => setIsRendered(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  // Touch handlers apenas para o Bottom Sheet (Mobile)
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isMobile || !sheetRef.current) return;
    dragRef.current = e.touches[0].clientY;
    currentYRef.current = e.touches[0].clientY;
    isDraggingRef.current = true;
    sheetRef.current.style.transition = 'none';
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDraggingRef.current || !sheetRef.current) return;
    currentYRef.current = e.touches[0].clientY;
    const diff = currentYRef.current - dragRef.current;

    // Só permitir arrastar para baixo
    if (diff > 0) {
      sheetRef.current.style.transform = `translateY(${diff}px)`;
    }
  };

  const handleTouchEnd = () => {
    if (!isDraggingRef.current || !sheetRef.current) return;
    isDraggingRef.current = false;
    sheetRef.current.style.transition = 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1)';

    const diff = currentYRef.current - dragRef.current;
    if (diff > 100) {
      onClose();
    } else {
      sheetRef.current.style.transform = 'translateY(0)';
    }
  };

  if (!isRendered) return null;

  if (!isMobile) {
    // Desktop: Modal Centralizado
    return (
      <div
        className={`fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
        style={{ pointerEvents: 'auto' }}
      >
        <div
          className={`bg-gradient-to-br from-[#1a1c23] to-[#0f1115] rounded-3xl border border-white/10 shadow-2xl relative transition-all duration-300 transform ${isVisible ? 'scale-100 translate-y-0 opacity-100' : 'scale-95 translate-y-4 opacity-0'} ${className}`}
          style={{ 
            width: '100%', 
            maxWidth: '480px', 
            maxHeight: fullHeight ? '95vh' : maxHeight, 
            display: 'flex', 
            flexDirection: 'column',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header Desktop */}
          {title && (
            <div className="flex items-center justify-between p-6 border-b border-white/5 flex-shrink-0">
              <h2 className="text-xl font-bold text-white tracking-tight">{title}</h2>
              <button 
                onClick={onClose} 
                className="text-gray-400 hover:text-white transition-all p-2 hover:bg-white/10 rounded-xl active:scale-90"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          )}
          {!title && (
            <button 
              onClick={onClose} 
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition-all p-2 hover:bg-white/10 rounded-full bg-black/40 z-20 active:scale-90"
            >
              <X className="h-5 w-5" />
            </button>
          )}

          <div className="flex-1 overflow-y-auto min-h-0 custom-scrollbar">
            {children}
          </div>
        </div>
      </div>
    );
  }

  // Mobile: Bottom Sheet Nativo
  const finalMaxHeight = fullHeight ? 'calc(100dvh - 20px)' : (maxHeight.includes('vh') ? maxHeight.replace('vh', 'dvh') : maxHeight);

  return (
    <div
      className={`fixed inset-0 z-[60] flex flex-col justify-end bg-black/70 backdrop-blur-sm transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{ pointerEvents: 'auto' }}
    >
      <div
        ref={sheetRef}
        className={`bg-[#111318] rounded-t-[32px] shadow-[0_-8px_30px_rgba(0,0,0,0.6)] border-t border-white/10 flex flex-col transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] transform ${isVisible ? 'translate-y-0' : 'translate-y-full'} ${className}`}
        style={{ 
          paddingBottom: 'max(env(safe-area-inset-bottom), 16px)', 
          maxHeight: finalMaxHeight,
          height: fullHeight ? finalMaxHeight : 'auto',
          willChange: 'transform'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Area de arraste premium */}
        <div
          className="w-full pt-3 pb-5 flex justify-center flex-shrink-0 touch-none"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="w-10 h-1.5 bg-white/20 rounded-full" />
        </div>

        {/* Header Mobile */}
        {title && (
          <div className="flex items-center justify-between px-6 pb-4 border-b border-white/5 flex-shrink-0">
            <h2 className="text-xl font-bold text-white tracking-tight truncate flex-1">{title}</h2>
            <button 
              onClick={onClose} 
              className="ml-4 text-white/50 hover:text-white transition-all p-2 bg-white/5 rounded-full active:scale-90"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        )}
        {!title && (
          <button 
            onClick={onClose} 
            className="absolute top-3 right-4 text-white/50 hover:text-white transition-all p-2 bg-white/5 rounded-full z-10 active:scale-90"
          >
            <X className="h-5 w-5" />
          </button>
        )}

        <div className="flex-1 overflow-y-auto overscroll-contain px-1">
          {children}
        </div>
      </div>
    </div>
  );
};

export default BottomSheet;
